import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import baseState from './baseState';
import tuner from '../tuner';
import { unwrapStream } from '../utils/streamUnwrap';
import { reportStreamStatus } from '../loggers/streamStatus';
import logCategoryActionLabel from '../loggers/logCategoryActionLabel';
import { actions } from '../player/constants/logging';
import debug from '../debugger';
import isHttpStream from '../utils/isHttpStream';
import processTransitionStream from '../utils/processTransitionStream';
import listenTimeLogger from '../loggers/listenTime';
import streamProcessor from '../streamProcessor';
import streamMapper from '../streamMapper';
import cancelable from '../utils/cancelable';
import playerName from '../player/constants/playerName';
import transitionStreamName from '../player/constants/transitionStreamName';
import nowPlayingPoller from '../nowPlayingPoller';
import { sendTimingEvent } from '../loggers/timing';

let currentStream;
let shouldLogStatus;
let streams;
let streamUnwrapPromise;
let wasPreviouslyPlaying = false;
let onlyHttpStreams = true;
let processCurrentStream;

function setIfLastStream(remainingStreams, stream = {}) {
  if (remainingStreams.length === 0) {
    stream.isFinalStream = true;
  }
}

function logStreamStatus(err, duration) {
  if (!shouldLogStatus) return;

  reportStreamStatus(
    tuner.listenId,
    currentStream,
    tuner.tune,
    err,
    duration,
    onlyHttpStreams,
  );
}

function onStreamProcessSuccess({ duration }) {
  debug.log(`took ${duration}ms to successfully process stream`);
  logStreamStatus(null, duration); // no error to pass here, so passing null.

  listenTimeLogger.setReportStartTime();
  tuner.changeState(tuner.states.playing);
}

function onStreamProcessFailure(error = {}) {
  // TUNE-14712: Sentry reports indicate that `error` is `null` in some cases,
  //  so adding an extra fallback here
  const errorToUse = error || {};
  const wasHlsAdvanced = get(currentStream, 'isHlsAdvanced');

  tuner.trigger(tuner.events.streamFailed);
  logStreamStatus(errorToUse);
  currentStream = streams.shift();

  if (currentStream) {
    setIfLastStream(streams, currentStream);

    const { isInstreamLoggingEnabled } = tuner.streamPlayer.getPlayerConfig();

    if (wasHlsAdvanced && isInstreamLoggingEnabled) {
      logCategoryActionLabel({ action: actions.trackingURLStreamSwitch });
    }

    processCurrentStream();
  } else {
    errorToUse.failedWithHttp = onlyHttpStreams && !wasPreviouslyPlaying;
    tuner.changeState(tuner.states.failed, errorToUse);
  }
}

function onStreamsUnwrapped(response) {
  const { isLiveSeekStream, isHlsAdvanced, isMidrollEnabled } =
    currentStream || {};
  const responseStreams = get(response, 'data.streams');
  return streamMapper.fromUnwrapResponse(
    responseStreams,
    tuner.forceHttpsStreams,
    {
      isLiveSeekStream,
      isHlsAdvanced,
      isMidrollEnabled,
    },
  );
}

function onError(errorData) {
  tuner.trigger(tuner.events.error, errorData);
}

function onPlayerSelected(_playerName) {
  // TODO: the InstreamAdPlayer playerName is already emitted in streamProcessor.process(). This
  //  conditional can be removed once the HLSAdvanced code in streamProcess.process() is moved to
  //  InstreamAdPlayer preload/load.
  if (_playerName !== playerName.InstreamAdPlayer) {
    tuner.trigger(tuner.events.playerSelected, _playerName);
  }
}

function processUnwrappedStreams(unwrappedStreams) {
  const stream = unwrappedStreams.shift();

  if (stream) {
    if (get(currentStream, 'isFinalStream')) {
      setIfLastStream(unwrappedStreams, stream);
    }
    onlyHttpStreams = onlyHttpStreams && isHttpStream(stream.url);

    const streamTimingStart = performance.now();
    streamProcessor
      .process(stream)
      .then((result) => {
        sendTimingEvent('loadAndPlayStream', streamTimingStart);
        return onStreamProcessSuccess(result);
      })
      .catch(() => {
        processUnwrappedStreams(unwrappedStreams);
      });
  } else {
    onStreamProcessFailure(new Error('all unwrapped streams failed'));
  }
}

processCurrentStream = function () {
  const { requiresUnwrap, url } = currentStream || {};

  tuner.trigger(tuner.events.evaluateStreamStart, Date.now());

  if (requiresUnwrap) {
    const unwrapTimingStart = performance.now();
    const streamUnwrapRequest = unwrapStream(url)
      .then((response) => {
        sendTimingEvent('unwrapStream', unwrapTimingStart);
        return onStreamsUnwrapped(response);
      })
      .then(processUnwrappedStreams)
      .catch(onStreamProcessFailure);

    streamUnwrapPromise = cancelable(streamUnwrapRequest);
  } else {
    const streamTimingStart = performance.now();
    streamProcessor
      .process(currentStream)
      .then((result) => {
        sendTimingEvent('loadAndPlayStream', streamTimingStart);
        return onStreamProcessSuccess(result);
      })
      .catch(onStreamProcessFailure);
  }
};

async function playBoostTransition(
  streamConfig,
  streamEvents,
  { resolve, reject },
) {
  try {
    nowPlayingPoller.showBoostTransitionTitle(
      streamConfig.nowPlayingTitleOverride,
    );
    tuner.trigger(streamEvents.initEvent);

    await processTransitionStream({
      ...streamConfig,
      isBoostTransition: true,
    });

    if (resolve) {
      resolve();
    }
  } catch (transitionStreamError) {
    const errorResult = reject || resolve;
    tuner.trigger(streamEvents.endEvent);

    if (errorResult) {
      errorResult();
    }
  }
}

function playBoostStream({ shouldLog, wasPlaying }) {
  const { stream = {}, parentGuideId } = tuner.boostState;
  currentStream = stream;
  wasPreviouslyPlaying = wasPlaying;
  shouldLogStatus = shouldLog;

  tuner.trigger(tuner.events.connecting, parentGuideId);
  tuner.streamPlayer.on(tuner.streamPlayer.events.error, onError);
  tuner.streamPlayer.on(
    tuner.streamPlayer.events.playerSelected,
    onPlayerSelected,
  );
  processCurrentStream();
}

async function connectBoostExperience({
  skipBoostIntro,
  shouldLog,
  wasPlaying,
}) {
  const { introMp3Url, introCap, introTitle } = tuner.boostConfig;
  const canPlayBasedOnIntroCap =
    introCap < 0 || tuner.boostState.introCount < introCap;

  if (skipBoostIntro || !canPlayBasedOnIntroCap) {
    nowPlayingPoller.toggleBoostMetadata(true);
    return playBoostStream({ shouldLog, wasPlaying });
  }

  try {
    await new Promise((resolve, reject) => {
      playBoostTransition(
        {
          stream: introMp3Url,
          transitionStreamName: transitionStreamName.intro,
          nowPlayingTitleOverride: introTitle,
        },
        {
          initEvent: tuner.events.initiateIntro,
          endEvent: tuner.events.endIntro,
        },
        { resolve, reject },
      );
    });
  } catch (e) {
    return playBoostStream({ shouldLog, wasPlaying });
  }

  // NOTE: Reset state machine to 'connecting' after successful intro transition
  // This is done to preserve the expected player UI &&
  // to direct the tuner to attemptPlayback on the chosen content stream
  tuner.boostState.introCount++;
  tuner.changeState(tuner.states.connecting, {
    shouldLogStatus: true,
    playBoostExperience: true,
    skipBoostIntro: true,
  });
}

function onMetadata(metadata) {
  nowPlayingPoller.setMetadataOverrides(metadata);
}

const state = {
  name: 'connecting',
  enter: function (previousState, context) {
    const { guideId, testStreamUrl, testStreamFormat } = get(tuner, 'tune', {});
    const {
      shouldLogStatus: shouldLog,
      forceShoutcast,
      wasPlaying,
      playBoostExperience,
      skipBoostIntro,
      onBoostOutroEnded,
    } = context || {};

    if (playBoostExperience) {
      // NOTE: connectBoostExperience starts the Boost Experience (Possible Intro + Content Stream Playback)
      connectBoostExperience({
        skipBoostIntro,
        shouldLog,
        wasPlaying,
      });
    } else if (onBoostOutroEnded) {
      const { outroMp3Url, outroTitle } = tuner.boostConfig;

      playBoostTransition(
        {
          stream: outroMp3Url,
          transitionStreamName: transitionStreamName.outro,
          nowPlayingTitleOverride: outroTitle,
        },
        {
          initEvent: tuner.events.initiateOutro,
          endEvent: tuner.events.endOutro,
        },
        { resolve: onBoostOutroEnded },
      );
    } else {
      tuner.trigger(tuner.events.connecting, guideId);
      wasPreviouslyPlaying = wasPlaying;

      /*
        The testStreamUrl is only passed in to test single
        streams. We attempt to play the test stream and
        ignore the regular functionality.
      */
      if (testStreamUrl) {
        tuner.streams = [
          {
            url: testStreamUrl,
            requiresUnwrap: false,
            isFinalStream: true,
            format: testStreamFormat || 'mp3', // default for test streams
            position: 0,
          },
        ];
      }

      shouldLogStatus = shouldLog;

      // forceShoutcast is set in playing.js when stream errors out.
      // Used in streamProcessor to force shoutcast hack.
      if (forceShoutcast) {
        currentStream.forceShoutcast = true;
      } else {
        streams = cloneDeep(get(tuner, 'streams', []));

        // Specify the index of the stream relative to the array of streams returned in the Tune.ashx
        // call. This will be used in the stream success/failure report.
        streams.forEach((stream, index) => {
          stream.index = index;
          // if a stream requires unwrap then the stream.url is not the actual stream url
          // so the http check will happen in processUnwrappedStreams function
          if (!stream.requiresUnwrap) {
            onlyHttpStreams = onlyHttpStreams && isHttpStream(stream.url);
          }
        });

        currentStream = streams.shift();

        setIfLastStream(streams, currentStream);
      }

      tuner.streamPlayer.on(tuner.streamPlayer.events.error, onError);
      tuner.streamPlayer.on(
        tuner.streamPlayer.events.playerSelected,
        onPlayerSelected,
      );
      tuner.streamPlayer.on(tuner.streamPlayer.events.metadata, onMetadata);

      processCurrentStream();
    }
  },
  exit: function () {
    onlyHttpStreams = true;
    wasPreviouslyPlaying = false;
    tuner.streamPlayer.remove(tuner.streamPlayer.events.error, onError);
    tuner.streamPlayer.remove(
      tuner.streamPlayer.events.playerSelected,
      onPlayerSelected,
    );
    tuner.streamPlayer.remove(tuner.streamPlayer.events.metadata, onMetadata);
    streamProcessor.cancel();
    if (streamUnwrapPromise) streamUnwrapPromise.cancel();
  },
};

export default {
  ...baseState,
  ...state,
};
